import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { mutate } from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import ModuleHelpHeader from '../../components/ModuleHelpHeader/ModuleHelpHeader';
import {
  importClientsFromContacts,
  previewClientsImport,
  type ImportContactsPreviewResult,
  type ImportContactsResult,
} from '../clients/useData';
import { moduleConfig } from '../clients/config';
import {
  parseContactsSpreadsheet,
  type ParsedContactsImportRow,
} from '../clients/utils/parseContactsSpreadsheet';

type Step = 'upload' | 'review' | 'importing' | 'done';

const BATCH_SIZE = 25;

function mergeResults(
  acc: ImportContactsResult,
  batch: ImportContactsResult,
): ImportContactsResult {
  return {
    created: acc.created + batch.created,
    skipped: acc.skipped + batch.skipped,
    failed: acc.failed + batch.failed,
    errors: [...acc.errors, ...batch.errors].slice(0, 100),
  };
}

const ImportOrganizationsPage: React.FC = () => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedContactsImportRow[]>([]);
  const [preview, setPreview] = useState<ImportContactsPreviewResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportContactsResult | null>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setFileName(null);
    setParsedRows([]);
    setPreview(null);
    setImportProgress({ done: 0, total: 0 });
    setImportResult(null);
    setSkipDuplicates(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const analyzeFile = async (file: File) => {
    setAnalyzing(true);
    setPreview(null);
    setImportResult(null);
    try {
      const parsed = await parseContactsSpreadsheet(file);
      const previewResult = await previewClientsImport(parsed.rows);
      setFileName(file.name);
      setParsedRows(parsed.rows);
      setPreview(previewResult);
      setStep('review');
    } catch (err) {
      console.error(err);
      showError(err instanceof Error ? err.message : 'Could not analyze spreadsheet');
      reset();
    } finally {
      setAnalyzing(false);
    }
  };

  const rowsToImport = useMemo(() => {
    if (!preview) return [];
    const readyIds = new Set(
      preview.rows.filter((r) => r.status === 'ready').map((r) => r.recordId),
    );
    if (skipDuplicates) {
      return parsedRows.filter((r) => readyIds.has(r.recordId));
    }
    return parsedRows.filter((r) => {
      const row = preview.rows.find((p) => p.recordId === r.recordId);
      return row && row.status !== 'invalid';
    });
  }, [preview, parsedRows, skipDuplicates]);

  const runImport = async () => {
    if (!rowsToImport.length) return;
    setStep('importing');
    setImportProgress({ done: 0, total: rowsToImport.length });
    let aggregate: ImportContactsResult = { created: 0, skipped: 0, failed: 0, errors: [] };

    try {
      for (let i = 0; i < rowsToImport.length; i += BATCH_SIZE) {
        const batch = rowsToImport.slice(i, i + BATCH_SIZE);
        const result = await importClientsFromContacts(batch, skipDuplicates);
        aggregate = mergeResults(aggregate, result);
        setImportProgress({
          done: Math.min(i + batch.length, rowsToImport.length),
          total: rowsToImport.length,
        });
      }
      setImportResult(aggregate);
      await mutate(moduleConfig.module.api.list);
      setStep('done');
    } catch (err) {
      console.error(err);
      showError('Import failed. Please try again.');
      setStep('review');
    }
  };

  const progressPct =
    importProgress.total > 0
      ? Math.round((importProgress.done / importProgress.total) * 100)
      : 0;

  const duplicateTotal =
    preview?.summary.duplicateInDb != null && preview?.summary.duplicateInFile != null
      ? preview.summary.duplicateInDb + preview.summary.duplicateInFile
      : preview?.summary.duplicateCount ?? 0;

  return (
    <div className={cn('w-full max-w-[90rem] mx-auto px-4 py-6 sm:px-6 space-y-6', colors.text)}>
      <ModuleHelpHeader
        title="Import organizations"
        description="Upload an Excel contacts export (.xls / .xlsx). The file is validated before anything is inserted. Rows with multiple emails add each address to the same organization. Expected columns: Name, e-mail, Phone, Fax, Address, Notes."
      />

      {step === 'upload' && (
        <section
          className={cn(
            'rounded-xl border p-8 flex flex-col items-center justify-center text-center min-h-[280px]',
            colors.border,
            colors.surface,
          )}
        >
          {analyzing ? (
            <>
              <Loader2 className="w-10 h-10 animate-spin mb-4 text-indigo-500" aria-hidden />
              <p className="font-medium">Analyzing spreadsheet…</p>
              <p className={cn('text-sm mt-1', colors.textMuted)}>Checking rows and duplicates</p>
            </>
          ) : (
            <>
              <FileSpreadsheet className={cn('w-12 h-12 mb-4', colors.textMuted)} aria-hidden />
              <p className="font-medium mb-2">Choose a contacts spreadsheet</p>
              <p className={cn('text-sm mb-6 max-w-md', colors.textMuted)}>
                Names starting with <code className="text-xs">C |</code> are kept as-is. Other names
                like <code className="text-xs">ORG | CONTACT</code> split into organization and
                contact person.
              </p>
              <Button type="button" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" aria-hidden />
                Select file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void analyzeFile(file);
                }}
              />
            </>
          )}
        </section>
      )}

      {preview && (step === 'review' || step === 'importing' || step === 'done') && (
        <>
          <section className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <p className="font-medium">{fileName}</p>
                <p className={cn('text-sm', colors.textMuted)}>
                  {preview.summary.total} rows analyzed
                  {preview.summary.existingInDatabase != null
                    ? ` · ${preview.summary.existingInDatabase} already in database`
                    : ''}
                </p>
              </div>
              {step === 'review' && (
                <Button type="button" variant="secondary" size="sm" onClick={reset}>
                  Choose another file
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {[
                { label: 'Ready to import', value: preview.summary.readyToImport, tone: 'ok' },
                { label: 'In database', value: preview.summary.duplicateInDb, tone: 'warn' },
                { label: 'Duplicate in file', value: preview.summary.duplicateInFile, tone: 'warn' },
                { label: 'Invalid', value: preview.summary.invalid, tone: 'bad' },
                { label: 'With email', value: preview.summary.withEmail, tone: 'neutral' },
                { label: 'With phone', value: preview.summary.withPhone, tone: 'neutral' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={cn(
                    'rounded-lg border px-3 py-2.5',
                    colors.border,
                    stat.tone === 'ok' && 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20',
                    stat.tone === 'warn' && 'border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20',
                    stat.tone === 'bad' && 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
                  )}
                >
                  <div className="text-lg font-semibold tabular-nums">{stat.value}</div>
                  <div className={cn('text-xs mt-0.5', colors.textMuted)}>{stat.label}</div>
                </div>
              ))}
            </div>

            {rowsToImport.length === 0 && step === 'review' && (
              <div
                className={cn(
                  'mt-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  'border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/25 text-amber-900 dark:text-amber-100',
                )}
              >
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
                <span>
                  Nothing can be imported from this file
                  {duplicateTotal > 0 ? ' (all rows are duplicates or invalid)' : ''}.
                  Fix the file or disable skip duplicates if you intend to re-import.
                </span>
              </div>
            )}

            {rowsToImport.length > 0 && step === 'review' && (
              <div
                className={cn(
                  'mt-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  'border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/25 text-emerald-900 dark:text-emerald-100',
                )}
              >
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
                <span>
                  {rowsToImport.length} organization{rowsToImport.length === 1 ? '' : 's'} can be
                  inserted
                  {skipDuplicates ? ' (duplicates will be skipped)' : ''}.
                </span>
              </div>
            )}
          </section>

          {step === 'review' && (
            <>
              <label className={cn('flex items-center gap-2 text-sm', colors.text)}>
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                />
                Skip rows that already exist or repeat in the file
              </label>

              <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
                <div className={cn('px-4 py-2 border-b text-sm font-medium', colors.border, colors.surfaceHover)}>
                  Row preview
                </div>
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className={cn('sticky top-0', colors.surfaceHover)}>
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Name</th>
                        <th className="text-left px-3 py-2 font-medium">Contact</th>
                        <th className="text-left px-3 py-2 font-medium">Email</th>
                        <th className="text-left px-3 py-2 font-medium">Phone</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.slice(0, 150).map((row) => {
                        const parsed = parsedRows.find((p) => p.recordId === row.recordId);
                        const emails = parsed?.contactPoints
                          .filter((c) => c.kind === 'EMAIL')
                          .map((c) =>
                            c.label && c.label.toLowerCase() !== 'work'
                              ? `${c.value} (${c.label})`
                              : c.value,
                          ) ?? [];
                        const phones = parsed?.contactPoints
                          .filter((c) => c.kind === 'PHONE')
                          .map((c) => c.value) ?? [];
                        return (
                          <tr key={row.recordId} className={cn('border-t', colors.border)}>
                            <td className="px-3 py-2 align-top">{row.clientName || row.sourceName}</td>
                            <td className="px-3 py-2 align-top">{row.contactPerson || '—'}</td>
                            <td className={cn('px-3 py-2 align-top text-xs whitespace-pre-line', colors.textMuted)}>
                              {emails.length > 0 ? emails.join('\n') : '—'}
                            </td>
                            <td className={cn('px-3 py-2 align-top text-xs whitespace-pre-line', colors.textMuted)}>
                              {phones.length > 0 ? phones.join('\n') : '—'}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {row.status === 'ready' && (
                                <span className="text-emerald-600 dark:text-emerald-400">Ready</span>
                              )}
                              {row.status === 'duplicate_db' && (
                                <span className="text-amber-600 dark:text-amber-400">In database</span>
                              )}
                              {row.status === 'duplicate_file' && (
                                <span className="text-amber-600 dark:text-amber-400">Duplicate in file</span>
                              )}
                              {row.status === 'invalid' && (
                                <span className="text-red-600 dark:text-red-400">Invalid</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {preview.rows.length > 150 && (
                  <p className={cn('px-4 py-2 text-xs border-t', colors.border, colors.textMuted)}>
                    Showing first 150 of {preview.rows.length} rows.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={runImport}
                  disabled={rowsToImport.length === 0}
                >
                  Import {rowsToImport.length > 0 ? `${rowsToImport.length} organizations` : ''}
                </Button>
                <Button type="button" variant="secondary" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </>
          )}

          {step === 'importing' && (
            <section className={cn('rounded-xl border p-6 space-y-4', colors.border, colors.surface)}>
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" aria-hidden />
                <div>
                  <p className="font-medium">Importing…</p>
                  <p className={cn('text-sm', colors.textMuted)}>
                    {importProgress.done} of {importProgress.total} processed
                  </p>
                </div>
              </div>
              <div className={cn('h-2 rounded-full overflow-hidden', colors.surfaceHover)}>
                <div
                  className="h-full bg-indigo-500 transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className={cn('text-sm tabular-nums', colors.textMuted)}>{progressPct}%</p>
            </section>
          )}

          {step === 'done' && importResult && (
            <section className={cn('rounded-xl border p-6 space-y-4', colors.border, colors.surface)}>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" aria-hidden />
                <div>
                  <p className="text-lg font-semibold">Import complete</p>
                  <p className={cn('text-sm', colors.textMuted)}>
                    Created {importResult.created} · Skipped {importResult.skipped}
                    {importResult.failed > 0 ? ` · Failed ${importResult.failed}` : ''}
                  </p>
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div className={cn('rounded-lg border px-3 py-2 text-sm max-h-40 overflow-auto', colors.border)}>
                  <p className="font-medium mb-1">Errors</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {importResult.errors.map((e) => (
                      <li key={e.recordId}>
                        Row {e.recordId}: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <Link to="/clients">
                  <Button type="button">View organizations</Button>
                </Link>
                <Button type="button" variant="secondary" onClick={reset}>
                  Import another file
                </Button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ImportOrganizationsPage;
